import React from 'react';
import { Edit2, Trash2, Bell, Calendar, Users, Building2, Info, CheckCircle, AlertCircle } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import type { Notification } from '../../types/notification';
import { VERTICALS } from '../../../../theme/constants';
import BrandLoader from '../../../../components/common/BrandLoader';

interface NotificationsListProps {
  notifications: Notification[];
  onEdit: (notification: Notification) => void;
  onDelete: (notificationId: string) => Promise<void>;
  loading: boolean;
}

const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications,
  onEdit,
  onDelete,
  loading
}) => {
  const formatDate = (date: Date) => {
    return dayjs(date).locale('es').format('D [de] MMMM [de] YYYY, HH:mm');
  };

  const getTargetLabel = (notification: Notification) => {
    switch (notification.targetType) {
      case 'all':
        return 'Todos los negocios';
      case 'vertical':
        return `Vertical: ${VERTICALS.find(v => v.id === notification.targetValue)?.name || notification.targetValue || 'Desconocido'}`;
      case 'specific':
        return 'Negocio específico';
      default:
        return 'Desconocido';
    }
  };

  // Function to determine notification style based on title or content
  const getNotificationStyle = (notification: { title?: string; message?: string }) => {
    const title = (notification.title || '').toLowerCase();
    const message = (notification.message || '').toLowerCase();
    
    if (title.includes('importante') || message.includes('importante') || 
        title.includes('urgente') || message.includes('urgente')) {
      return {
        bgColor: 'bg-red-50 dark:bg-red-500/10',
        borderColor: 'border-red-400',
        iconColor: 'text-red-500',
        icon: AlertCircle
      };
    } else if (title.includes('éxito') || message.includes('éxito') || 
              title.includes('completado') || message.includes('completado')) {
      return {
        bgColor: 'bg-green-50 dark:bg-green-500/10',
        borderColor: 'border-green-400',
        iconColor: 'text-green-500',
        icon: CheckCircle
      };
    } else {
      return {
        bgColor: 'bg-blue-50 dark:bg-blue-500/10',
        borderColor: 'border-blue-400',
        iconColor: 'text-blue-500',
        icon: Info
      };
    }
  };

  const getStatusBadgeColor = (notification: Notification) => {
    const now = new Date();
    const isActive = notification.status === 'active';
    const hasStarted = now >= notification.startDate;
    const hasEnded = notification.endDate ? now > notification.endDate : false;

    if (!isActive) return 'bg-canvas text-content';
    if (!hasStarted) return 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300';
    if (hasEnded) return 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300';
    return 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300';
  };

  const getStatusLabel = (notification: Notification) => {
    const now = new Date();
    const isActive = notification.status === 'active';
    const hasStarted = now >= notification.startDate;
    const hasEnded = notification.endDate ? now > notification.endDate : false;

    if (!isActive) return 'Borrador';
    if (!hasStarted) return 'Programado';
    if (hasEnded) return 'Finalizado';
    return 'Activo';
  };

  return (
    <div className="overflow-hidden">
      {loading ? (
        <div className="text-center py-6">
          <BrandLoader size={36} />
          <p className="mt-2 text-sm text-content-3">Cargando notificaciones...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-8">
          <Bell className="h-12 w-12 text-content-3 mx-auto mb-3" />
          <p className="text-content-3">No hay notificaciones configuradas</p>
        </div>
      ) : (
        <div className="flow-root">
          <ul role="list" className="space-y-4">
            {notifications.map((notification) => {
              const style = getNotificationStyle(notification);
              const Icon = style.icon;
              
              return (
                <li 
                  key={notification.id} 
                  className={`${style.bgColor} border-l-4 ${style.borderColor} rounded-lg shadow-sm transition-all duration-300 hover:shadow-md overflow-hidden`}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-3">
                          <div className={`p-2 ${style.bgColor} rounded-full border border-${style.borderColor.split('-')[1]}`}>
                            <Icon className={`h-5 w-5 ${style.iconColor}`} />
                          </div>
                        </div>
                        <h3 className="text-base font-medium text-content flex items-center">
                          {notification.title}
                          <span className={`ml-2 w-2 h-2 ${style.iconColor} rounded-full`}></span>
                          <span 
                            className={`ml-2 text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusBadgeColor(notification)}`}
                          >
                            {getStatusLabel(notification)}
                          </span>
                        </h3>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onEdit(notification)}
                          className="text-accent hover:text-blue-900 bg-surface p-1.5 rounded-full shadow-sm hover:shadow transition-all"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(notification.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 bg-surface p-1.5 rounded-full shadow-sm hover:shadow transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-2 pl-10">
                      <p className="text-sm text-content-2 line-clamp-2 mb-3">
                        {notification.message}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 text-xs text-content-3">
                        <span className="flex items-center bg-surface px-2 py-1 rounded-full shadow-sm">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(notification.startDate)}
                        </span>
                        
                        {notification.endDate && (
                          <span className="flex items-center bg-surface px-2 py-1 rounded-full shadow-sm">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(notification.endDate)}
                          </span>
                        )}
                        
                        <span className="flex items-center bg-surface px-2 py-1 rounded-full shadow-sm">
                          {notification.targetType === 'all' ? (
                            <Users className="h-3 w-3 mr-1" />
                          ) : (
                            <Building2 className="h-3 w-3 mr-1" />
                          )}
                          {getTargetLabel(notification)}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationsList;